import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.js";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const NFE_REGEX = /\b\d{44}\b/g;
const NFE_FLEXIBLE_REGEX = /\d(?:[\s.-]*\d){43}/g;
const CHASSIS_REGEX = /\b([A-HJ-NPR-Z0-9]{17})\b/g;
const ONLY_DIGITS_REGEX = /\D/g;
const PDF_MIME_TYPES = new Set(["application/pdf"]);
const XML_MIME_TYPES = new Set(["application/xml", "text/xml"]);

const normalizeText = (chunks) =>
  chunks
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

const normalizePlainText = (text) =>
  (text || "")
    .toString()
    .replace(/\s+/g, " ")
    .trim();

const getFileExtension = (file) => {
  const name = (file?.name || "").toLowerCase();
  if (!name.includes(".")) {
    return "";
  }
  return name.substring(name.lastIndexOf(".") + 1);
};

const isPdfFile = (file) => {
  if (!file) {
    return false;
  }
  const type = (file.type || "").toLowerCase();
  if (PDF_MIME_TYPES.has(type)) {
    return true;
  }
  return getFileExtension(file) === "pdf";
};

const isXmlFile = (file) => {
  if (!file) {
    return false;
  }
  const type = (file.type || "").toLowerCase();
  if (XML_MIME_TYPES.has(type)) {
    return true;
  }
  return getFileExtension(file) === "xml";
};

const extractValues = (text, regex) => {
  const matches = text.match(regex);
  if (!matches || matches.length === 0) {
    return [];
  }

  return [...new Set(matches)];
};

const rankMatches = (matches) => (matches.length > 0 ? matches[0] : null);

const extractNfe = (text) => {
  const exactMatches = extractValues(text, NFE_REGEX);
  if (exactMatches.length > 0) {
    return rankMatches(exactMatches);
  }

  const flexibleMatches = text.match(NFE_FLEXIBLE_REGEX);
  if (!flexibleMatches) {
    return null;
  }

  const normalizedMatches = [
    ...new Set(
      flexibleMatches
        .map((candidate) => candidate.replace(ONLY_DIGITS_REGEX, ""))
        .filter((value) => value.length === 44),
    ),
  ];

  return rankMatches(normalizedMatches);
};

const extractDataFromText = (text) => {
  const normalized = normalizePlainText(text || "");
  return {
    normalizedText: normalized,
    nfe: extractNfe(normalized),
    chassis: rankMatches(extractValues(normalized, CHASSIS_REGEX)),
  };
};

export const extrairDadosPdf = async (file) => {
  try {
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    const textChunks = [];

    for (let pageIndex = 1; pageIndex <= pdf.numPages; pageIndex += 1) {
      const page = await pdf.getPage(pageIndex);
      const content = await page.getTextContent();
      const pageText = content.items.map((item) => item.str || "").join(" ");
      textChunks.push(pageText);
    }

    const text = normalizeText(textChunks);
    const { nfe, chassis, normalizedText } = extractDataFromText(text);

    return {
      success: true,
      nfe,
      chassis,
      rawText: normalizedText,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      nfe: null,
      chassis: null,
      rawText: "",
      error: error?.message || "Falha ao processar PDF.",
    };
  }
};

export const extrairDadosXml = async (file) => {
  try {
    const content = await file.text();
    const { nfe, chassis, normalizedText } = extractDataFromText(content);
    return {
      success: true,
      nfe,
      chassis,
      rawText: normalizedText,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      nfe: null,
      chassis: null,
      rawText: "",
      error: error?.message || "Falha ao processar XML.",
    };
  }
};

export const extrairDadosArquivo = async (file) => {
  if (isPdfFile(file)) {
    return extrairDadosPdf(file);
  }
  if (isXmlFile(file)) {
    return extrairDadosXml(file);
  }
  return {
    success: false,
    nfe: null,
    chassis: null,
    rawText: "",
    error: "Formato de arquivo não suportado. Utilize PDF ou XML.",
  };
};

export const extrairDadosPdfEmLote = async (files) => {
  const resultados = await Promise.all(files.map(extrairDadosArquivo));
  return resultados;
};

export const isPdfOrXmlFile = (file) => isPdfFile(file) || isXmlFile(file);
export { isPdfFile, isXmlFile };
